import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, model, output, signal, Signal } from '@angular/core';
import { ButtonSize, ButtonType } from './button.types';

@Component({
  selector: 'mbs-button, [mbs-button]',
  templateUrl: 'button.component.html',
  host: {
    '[class.-disabled]': 'disabledOrLoading()',
    '[class.-isBlock]': 'block()',
    '[class.-isCtrl]': 'isCtrl()'
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, NgTemplateOutlet]
})
export class ButtonComponent {
  /**
   * Button type. Possible values: 'button' | 'reset' | 'submit'.
   * By default is `button`
   */
  public readonly buttonType = model<'button' | 'reset' | 'submit'>('button');

  /**
   * Show the button as a link
   * If `true` it will use `ctrl` class instead of `btn`
   */
  public readonly isCtrl = input<boolean>(false);

  /**
   * Show button as a dashed button
   * If `true` it will use `dashed` class instead of `btn`
   */
  public readonly isDashed = input<boolean>(false);

  /**
   * Implements one of bootstrap's themes to button <br>
   * Possible values `primary | secondary | success | danger | warning | info | light | dark | link`
   * <br><br>
   * Defaults: <br>
   * If `isCtrl` is `true` - `primary` <br>
   * Else - `secondary`
   */
  public readonly type = model<ButtonType>(null);

  /**
   * Implements one of the button sizes <br />
   * Possible values for btn: `'xxs' | 'xs' | 'sm' | 'md' | 'lg'`<br />
   * Possible values for ctrl: `'inherit' | 'xxs' | 'xs' | 'sm' | 'md' | 'lg'`
   */
  public readonly size = input<ButtonSize>(null);

  /**
   * If `true` button will have `display: flex` style
   */
  public readonly block = input<boolean>(false);

  /**
   * If `true` replaces the button's content with an animated loader and disables the button.
   */
  public readonly loading = model<boolean>(false);

  /**
   * If `true` disables button
   */
  public readonly disabled = model<boolean>(false);

  /**
   * CSS class(es) for an icon. For now the icon is always placed on the left of button content
   */
  public readonly icon = input<string>(null);

  /**
   * If `true` button will have `active` class
   */
  public readonly active = model<boolean>(false);

  /**
   * Button's id
   */
  public readonly id = input<string>(null);

  /**
   * If `true` text is truncated with ellipsis
   * @default null
   */
  public readonly textOverflow = input<boolean>(null);

  /**
   * Custom CSS classes for a button
   */
  public readonly customClasses = input<string>('');

  /**
   * Show square button
   * If `true` button will have `width` equal to `height`
   */
  public readonly square = input<boolean>(false);

  /**
   * Show a rounded button
   * If `true` button's border radius will be set to 50%
   */
  public readonly rounded = input<boolean>(false);

  /**
   * Alignment of the button text
   * Default is `left`
   */
  public readonly align = input<'left'>(null);

  /**
   * Emits when a button is clicked
   */
  public readonly click = output<MouseEvent>();
  public readonly clickCounter = signal<number>(0); // parent component can use this signal to track clicks on the button

  protected iconClasses: Signal<string> = computed(() => `${this.isCtrl() ? 'ctrl-ico' : 'btn-ico'} ${this.icon()}`);
  protected disabledOrLoading: Signal<boolean> = computed(() => this.disabled() || this.loading());
  protected buttonClasses: Signal<string[]> = computed(() => {
    const prefix = this.prefix();
    const size = this.size();
    const textOverflow = this.textOverflow();
    const type = this.type() ?? (this.isCtrl() ? 'primary' : 'secondary');

    return [
      prefix,
      `${prefix}-${type}`,
      size && `${prefix}-${size}`,
      this.block() && 'btn-flex',
      this.loading() && '-loading',
      this.active() && 'active',
      this.customClasses(),
      this.rounded() && 'btn-rounded',
      this.square() && 'btn-square',
      this.align() === 'left' && 'text-start',
      textOverflow == null ? '' : textOverflow ? 'text-overflow' : 'text-no-overflow'
    ].filter(Boolean);
  });

  private prefix: Signal<string> = computed(() => {
    if (this.isCtrl()) return 'ctrl';
    if (this.isDashed()) return 'dashed';

    return 'btn';
  });

  public handleClick(event: MouseEvent): void {
    event.stopPropagation();

    (event.target as HTMLElement)?.blur();

    if (this.buttonType() !== 'submit') {
      event.preventDefault();
    }

    this.clickCounter.update((count) => count + 1);
    this.click.emit(event);
  }
}
